import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    VIEW, EDIT,
    PUBLISHED, UNPUBLISHED,
    TYPE_A, TYPE_B,
    ASC,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import { QidProduct, QidProductCreate } from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import { qidProductsRoute } from "client/parts/routes"
import {
    StoreType,
    QidProductValidation,
    SortQidCompanies,
    SaveQidProduct,
    closeModal,
    setQidProductEdit,
    setQidProductFolderId,
    setQidProductReloadList,
    setQidProductReloadView,
    setQidProductReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    FileLoader,
    SelectDropdown,
    SelectDropdownItem,
    RadioButton,
    CheckButton,
    CheckboxWrapper,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditProductProps {
    product: QidProduct
}

let isMounted: boolean

const Form = ({
    product,
}: EditProductProps) => {
    const company = useSelector((state: StoreType) => state.qidCompany.companyView)
    const companyList = useSelector((state: StoreType) => state.qidCompany.companyList)
    const [companyIds] = useState(SortQidCompanies(Object.values(companyList), "name", ASC, {}))

    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(product.name)

    const [companyId, setCompanyId] = useState((company && company.id) || product.companyId || null)
    const [logoId, setLogoId] = useState(product.logoId)
    const [logo, setLogo] = useState(product.logo)
    const [type, setType] = useState(product.type)
    const [lowDemandMode, setLowDemandMode] = useState(product.lowDemandMode)
    const [highDemandMode, setHighDemandMode] = useState(product.highDemandMode)
    const [continuousMode, setContinuousMode] = useState(product.continuousMode)
    const [logicSolver, setLogicSolver] = useState(product.logicSolver)
    const [provenInUse, setProvenInUse] = useState(product.provenInUse)
    const [status, setStatus] = useState(product.status)

    const [validation, setValidation] = useState<QidProductValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = product.id
            ? `${qidProductsRoute}/${VIEW}/${product.id}`
            : qidProductsRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (saveMode: number) => {
        if (saveMode === 1) setSaveState(PENDING)
        if (saveMode === 2) setApplyState(PENDING)

        const newProductData: QidProductCreate = {
            companyId,
            name,
            logoId,
            type,
            lowDemandMode,
            highDemandMode,
            continuousMode,
            logicSolver,
            provenInUse,
            status,
        }

        const updated = product && product.id && (
            product.name !== name
            || product.logoId !== logoId
            || product.type !== type
            || product.lowDemandMode !== lowDemandMode
            || product.highDemandMode !== highDemandMode
            || product.continuousMode !== continuousMode
            || product.logicSolver !== logicSolver
            || product.provenInUse !== provenInUse
            || product.status !== status
        )

        SaveQidProduct(
            product.id,
            newProductData,
            userData.accountId,
            updated,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newProductId) replaceHistoryState(`${qidProductsRoute}/${EDIT}/${res.newProductId}`)
            }
            if (res.validation) return

            if (res.productFolderId) {
                dispatch(setQidProductFolderId(res.productFolderId))
            }

            dispatch(setQidProductEdit({
                id: res.newProductId || product.id,
                logo,
                ...newProductData,
            }))

            dispatch(setQidProductReloadList(false))
            dispatch(setQidProductReloadView(false))
            setTimeout(() => {
                dispatch(setQidProductReloadList(true))
                dispatch(setQidProductReloadView(true))
            }, 0)

            if (saveMode === 1) onClose()
            if (saveMode === 2) {
                dispatch(setQidProductReloadEdit(false))
                setTimeout(() => {
                    dispatch(setQidProductReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.company}</Label>
                        <SelectDropdown title={companyList[companyId] ? companyList[companyId].name : ""}>
                            {
                                companyIds.map((id) => (
                                    <SelectDropdownItem key={id} onClick={() => setCompanyId(id)}>
                                        {companyList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.companyId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.image}</Label>
                        <FileLoader
                            value={logo}
                            callback={(file) => {
                                setLogoId(file.id)
                                setLogo(file)
                            }}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.mode}</Label>
                        <CheckboxWrapper>
                            <CheckButton
                                checked={lowDemandMode}
                                onChange={() => setLowDemandMode(!lowDemandMode)}
                                title={lang.modeLowDemand}
                            />
                            <CheckButton
                                checked={highDemandMode}
                                onChange={() => setHighDemandMode(!highDemandMode)}
                                title={lang.modeHighDemand}
                            />
                            <CheckButton
                                checked={continuousMode}
                                onChange={() => setContinuousMode(!continuousMode)}
                                title={lang.modeContinuous}
                            />
                        </CheckboxWrapper>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.type}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={type === TYPE_A}
                                onChange={() => setType(TYPE_A)}
                                title={TYPE_A}
                            />
                            <RadioButton
                                checked={type === TYPE_B}
                                onChange={() => setType(TYPE_B)}
                                title={TYPE_B}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.logicSolver}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={logicSolver}
                                onChange={() => setLogicSolver(true)}
                                title={lang.yes}
                            />
                            <RadioButton
                                checked={!logicSolver}
                                onChange={() => setLogicSolver(false)}
                                title={lang.no}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.provenReliabilityData}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={provenInUse === PROVEN_NO}
                                onChange={() => setProvenInUse(PROVEN_NO)}
                                title={lang.no}
                            />
                            <RadioButton
                                checked={provenInUse === PROVEN_IN_USE}
                                onChange={() => setProvenInUse(PROVEN_IN_USE)}
                                title={lang.provenInUse}
                            />
                            <RadioButton
                                checked={provenInUse === PRIOR_USE}
                                onChange={() => setProvenInUse(PRIOR_USE)}
                                title={lang.priorUse}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={status === PUBLISHED}
                                onChange={() => setStatus(PUBLISHED)}
                                title={lang.published}
                            />
                            <RadioButton
                                checked={status === UNPUBLISHED}
                                onChange={() => setStatus(UNPUBLISHED)}
                                title={lang.unpublished}
                            />
                        </FormGroup>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
